import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { Download, Eye, FileSpreadsheet, FileText, LockKeyhole, Mail, SlidersHorizontal, X } from "lucide-react";
import CustomPagination from "../../components/CustomPagination";
import EmptyData from "../../components/EmptyData";
import {
  downloadFinanceDocumentsZip,
  emailFinanceDocuments,
  exportFinanceDocuments,
  getFinanceDocument,
  getFinanceDocumentPdf,
  getFinanceDocuments,
} from "../../api/financeDocuments";
import "./financeDocuments.css";

const DOCUMENT_TYPES = [
  ["COMMISSION_INVOICE", "Commission Invoice"],
  ["SELLER_SETTLEMENT_PAYOUT_STATEMENT", "Seller Settlement / Payout Statement"],
  ["SALES_INVOICE", "Sales Invoice"],
  ["CREDIT_NOTE", "Credit Note"],
  ["REFUND_DOCUMENT", "Refund Document"],
  ["CORRECTION_DOCUMENT", "Correction Document"],
  ["TRANSACTION_EXPORT", "Transaction Export"],
];

const EMPTY_FILTERS = { seller: "", dateFrom: "", dateTo: "", documentType: "", payoutBatch: "", orderNumber: "", status: "" };

const cleanObject = (value) => Object.fromEntries(Object.entries(value).filter(([, item]) => item !== "" && item !== null && item !== undefined));
const errorMessage = (error, fallback) => error?.response?.data?.message || error?.response?.data?.error || fallback;
const documentId = (item) => item?.id ?? item?.documentId ?? item?._id;
const displayValue = (value) => value === null || value === undefined || value === "" ? "—" : value;
const sellerName = (item) => item?.seller?.name || item?.sellerName || item?.seller_name || item?.seller || "—";
const sellerEmail = (item) => item?.seller?.email || item?.sellerEmail || item?.seller_email;
const orderNumber = (item) => item?.orderNumber ?? item?.order_number ?? item?.orderId ?? item?.order_id;
const payoutBatch = (item) => item?.payoutBatch ?? item?.payout_batch ?? item?.payoutBatchId ?? item?.payout_batch_id;
const issuedDate = (item) => item?.date || item?.issuedAt || item?.issued_at || item?.createdAt || item?.created_at;
const normalizeLabel = (value) => {
  if (!value) return "—";
  const knownType = DOCUMENT_TYPES.find(([key]) => key === value);
  if (knownType) return knownType[1];
  return String(value).replace(/[_-]+/g, " ").toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase());
};
const formatDocumentDate = (value) => {
  if (!value) return "—";
  const date = dayjs(value);
  return date.isValid() ? date.format("DD MMM YYYY") : "—";
};
const formatMoney = (amount, currency) => {
  const number = Number(amount);
  if (!Number.isFinite(number)) return "—";
  try { return new Intl.NumberFormat(undefined, { style: "currency", currency: currency || "EUR" }).format(number); }
  catch { return `${number.toFixed(2)} ${currency || ""}`.trim(); }
};

const unpackList = (response) => {
  const payload = response?.data?.data ?? response?.data ?? {};
  const documents = Array.isArray(payload) ? payload : payload.documents || payload.items || payload.rows || payload.results || [];
  const total = Number(payload.total ?? payload.totalCount ?? payload.count ?? response?.data?.total ?? documents.length);
  return { documents, total };
};

const saveBlob = (blob, filename) => {
  if (!(blob instanceof Blob) || !blob.size) throw new Error("The server returned an empty file.");
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const FinanceDocumentCentre = () => {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [documents, setDocuments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [fileAction, setFileAction] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [emailForm, setEmailForm] = useState({ scope: "documents", format: "csv", email: "" });
  const [emailLoading, setEmailLoading] = useState(false);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getFinanceDocuments(cleanObject({
        seller: appliedFilters.seller,
        date_from: appliedFilters.dateFrom,
        date_to: appliedFilters.dateTo,
        document_type: appliedFilters.documentType,
        payout_batch_id: appliedFilters.payoutBatch,
        order_id: appliedFilters.orderNumber,
        status: appliedFilters.status,
        page,
        limit,
      }));
      const result = unpackList(response);
      setDocuments(result.documents);
      setTotal(result.total);
      setSelected([]);
    } catch (error) {
      toast.error(errorMessage(error, "Unable to load finance documents."));
      setDocuments([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, page, limit]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const filterDebounceRef = useRef(null);

  // Auto-apply filters shortly after they change, instead of requiring a
  // manual "Search / Apply Filters" click.
  useEffect(() => {
    filterDebounceRef.current = setTimeout(() => {
      setPage(1);
      setAppliedFilters(filters);
    }, 400);
    return () => clearTimeout(filterDebounceRef.current);
  }, [filters]);

  const currentIds = useMemo(() => documents.map(documentId).filter((id) => id !== undefined), [documents]);
  const allSelected = currentIds.length > 0 && currentIds.every((id) => selected.includes(id));
  const updateFilter = (event) => setFilters((current) => ({ ...current, [event.target.name]: event.target.value }));
  const updateDate = (name, value) => setFilters((current) => ({ ...current, [name]: value?.isValid() ? value.format("YYYY-MM-DD") : "" }));

  const hasActiveFilters = Object.values(filters).some(Boolean);

  const resetFilters = () => {
    if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const toggleAll = () => setSelected(allSelected ? selected.filter((id) => !currentIds.includes(id)) : [...new Set([...selected, ...currentIds])]);
  const toggleOne = (id) => setSelected((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);

  const downloadPdf = async (item) => {
    const id = documentId(item);
    if (!id) return;
    setFileAction(`pdf-${id}`);
    try {
      const response = await getFinanceDocumentPdf(id);
      saveBlob(response.data, `${item.documentNumber || item.document_number || `finance-document-${id}`}.pdf`);
      toast.success("PDF downloaded successfully.");
    } catch (error) {
      toast.error(errorMessage(error, "Unable to download the PDF."));
    } finally { setFileAction(""); }
  };

  const viewDocument = async (item) => {
    const id = documentId(item);
    if (!id) return;
    setDetail(item);
    setDetailLoading(true);
    try {
      const response = await getFinanceDocument(id);
      setDetail(response?.data?.data ?? response?.data ?? item);
    } catch (error) { toast.error(errorMessage(error, "Unable to load document details.")); }
    finally { setDetailLoading(false); }
  };

  const runBulkDownload = async (type) => {
    if (!selected.length) return toast.error("Select at least one document.");
    setFileAction(type);
    try {
      const response = type === "zip"
        ? await downloadFinanceDocumentsZip({ documentIds: selected })
        : await exportFinanceDocuments({ documentIds: selected, format: type.toUpperCase() });
      saveBlob(response.data, `finance-documents-${new Date().toISOString().slice(0, 10)}.${type}`);
      toast.success(`${type.toUpperCase()} generated successfully.`);
    } catch (error) { toast.error(errorMessage(error, `Unable to generate ${type.toUpperCase()}.`)); }
    finally { setFileAction(""); }
  };

  const submitEmail = async (event) => {
    event.preventDefault();
    if (emailForm.scope === "documents" && !selected.length) return toast.error("Select at least one document.");
    setEmailLoading(true);
    try {
      await emailFinanceDocuments(cleanObject({
        email: emailForm.email,
        format: emailForm.format.toUpperCase(),
        documentIds: emailForm.scope === "documents" ? selected : undefined,
        filters: emailForm.scope === "filters" ? cleanObject({
          seller: appliedFilters.seller,
          date_from: appliedFilters.dateFrom,
          date_to: appliedFilters.dateTo,
          document_type: appliedFilters.documentType,
          payout_batch_id: appliedFilters.payoutBatch,
          order_id: appliedFilters.orderNumber,
          status: appliedFilters.status,
        }) : undefined,
      }));
      toast.success("Email export requested successfully.");
      setShowEmail(false);
      setEmailForm({ scope: "documents", format: "csv", email: "" });
    } catch (error) { toast.error(errorMessage(error, "Unable to request the email export.")); }
    finally { setEmailLoading(false); }
  };

  const onPageChange = useCallback((nextPage, nextLimit) => { setPage(nextPage); setLimit(nextLimit); }, []);

  return <div className="finance-centre container-fluid">
    <div className="finance-title"><div><p>Admin <span>/</span> Finance</p><h2>Finance Document Centre</h2><div className="finance-subtitle">Search, review and export issued financial documents.</div></div><div className="finance-title-icon"><FileText size={26} /></div></div>

    <div className="finance-card finance-filters">
      <div className="finance-section-heading"><div className="finance-section-icon"><SlidersHorizontal size={18} /></div><div><h3>Find documents</h3><p>Use one or more filters to narrow the results.</p></div></div>
      <div className="finance-filter-grid">
        <label>Seller<input name="seller" value={filters.seller} onChange={updateFilter} placeholder="Seller name or ID" /></label>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <label className="finance-date-field">Date From<DatePicker value={filters.dateFrom ? dayjs(filters.dateFrom) : null} onChange={(value) => updateDate("dateFrom", value)} maxDate={filters.dateTo ? dayjs(filters.dateTo) : undefined} format="DD/MM/YYYY" slotProps={{ textField: { size: "small", placeholder: "DD/MM/YYYY" } }} /></label>
          <label className="finance-date-field">Date To<DatePicker value={filters.dateTo ? dayjs(filters.dateTo) : null} onChange={(value) => updateDate("dateTo", value)} minDate={filters.dateFrom ? dayjs(filters.dateFrom) : undefined} format="DD/MM/YYYY" slotProps={{ textField: { size: "small", placeholder: "DD/MM/YYYY" } }} /></label>
        </LocalizationProvider>
        <label>Document Type<select name="documentType" value={filters.documentType} onChange={updateFilter}><option value="">All document types</option>{DOCUMENT_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        <label>Payout Batch<input name="payoutBatch" value={filters.payoutBatch} onChange={updateFilter} placeholder="Batch number" /></label>
        <label>Order Number<input name="orderNumber" value={filters.orderNumber} onChange={updateFilter} placeholder="Order number" /></label>
        <label>Payment / Status<input name="status" value={filters.status} onChange={updateFilter} placeholder="Paid, pending…" /></label>
      </div>
      <div className="finance-filter-clear">
        <button
          type="button"
          className="btn audit-clear-btn"
          onClick={resetFilters}
          disabled={!hasActiveFilters}
          style={{
            background: "#fff4ed",
            color: "#ff6b00",
            border: "1px solid #ffd4ba",
            opacity: hasActiveFilters ? 1 : 0.5,
          }}
        >
          <X size={16} /> Clear Filters
        </button>
      </div>
    </div>

    <div className="finance-card">
      <div className="finance-toolbar">
        <div className="finance-result-summary"><strong>Documents</strong><span>{total} {total === 1 ? "record" : "records"}</span><span className={`finance-selected-count ${selected.length ? "active" : ""}`}>{selected.length} selected</span></div>
        <div>
          <button className="finance-btn secondary" disabled={!selected.length || !!fileAction} onClick={() => runBulkDownload("zip")}>{fileAction === "zip" ? <Spinner size="sm" /> : <><Download size={15} /> Download ZIP</>}</button>
          <button className="finance-btn secondary" disabled={!selected.length || !!fileAction} onClick={() => runBulkDownload("csv")}>{fileAction === "csv" ? <Spinner size="sm" /> : <><FileSpreadsheet size={15} /> Export CSV</>}</button>
          <button className="finance-btn secondary" disabled={!selected.length || !!fileAction} onClick={() => runBulkDownload("xlsx")}>{fileAction === "xlsx" ? <Spinner size="sm" /> : <><FileSpreadsheet size={15} /> Export XLSX</>}</button>
          <button className="finance-btn primary" onClick={() => setShowEmail(true)}><Mail size={15} /> Email Export</button>
        </div>
      </div>
      <div className="finance-table-wrap"><table className="finance-table"><thead><tr><th><input type="checkbox" aria-label="Select all documents on page" checked={allSelected} onChange={toggleAll} /></th><th>Document Number</th><th>Document Type</th><th>Seller</th><th>Order Number</th><th>Payout Batch</th><th>Amount</th><th>Currency</th><th>Date</th><th>Status</th><th>Actions</th></tr></thead>
        <tbody>{!loading && documents.map((item) => { const id = documentId(item); const status = item.status || item.paymentStatus; const issued = String(status).toLowerCase() === "issued" || item.issued === true; const type = item.documentType || item.document_type; return <tr key={id} className={selected.includes(id) ? "selected" : ""}><td><input type="checkbox" aria-label={`Select ${item.documentNumber || item.document_number || id}`} checked={selected.includes(id)} onChange={() => toggleOne(id)} /></td><td><div className="finance-document-number"><span className="finance-file-icon"><FileText size={16} /></span><strong>{displayValue(item.documentNumber || item.document_number)}</strong>{issued && <span className="finance-lock" title="Issued documents are immutable"><LockKeyhole size={13} /></span>}</div></td><td><span className="finance-type">{normalizeLabel(type)}</span></td><td><div><strong className="finance-seller">{sellerName(item)}</strong>{sellerEmail(item) && <small className="finance-seller-email">{sellerEmail(item)}</small>}</div></td><td>{displayValue(orderNumber(item))}</td><td>{displayValue(payoutBatch(item))}</td><td><strong className="finance-amount">{formatMoney(item.amount, item.currency)}</strong></td><td>{displayValue(item.currency)}</td><td>{formatDocumentDate(issuedDate(item))}</td><td><span className={`finance-status ${String(status || "").toLowerCase()}`}>{normalizeLabel(status)}</span></td><td className="finance-actions"><button title="View document" onClick={() => viewDocument(item)}><Eye size={15} /> View</button><button title="Download PDF" disabled={!!fileAction} onClick={() => downloadPdf(item)}>{fileAction === `pdf-${id}` ? <Spinner size="sm" /> : <><Download size={15} /> PDF</>}</button></td></tr>; })}</tbody></table></div>
      {loading ? <div className="finance-loading"><Spinner animation="border" /></div> : !documents.length ? <EmptyData /> : <CustomPagination total={total} onPageChange={onPageChange} resetPage={false} />}
    </div>

    <Modal show={!!detail} onHide={() => setDetail(null)} centered size="lg" dialogClassName="finance-detail-modal"><Modal.Header closeButton><div className="finance-modal-heading"><div className="finance-modal-icon"><FileText size={24} /></div><div><span>Financial document</span><Modal.Title>{detail?.documentNumber || detail?.document_number || "Document details"}</Modal.Title></div></div></Modal.Header><Modal.Body>{detailLoading ? <div className="finance-loading"><Spinner /></div> : detail && <><div className="finance-detail-hero"><div><span className="finance-type">{normalizeLabel(detail.documentType || detail.document_type)}</span><h3>{sellerName(detail)}</h3><p>{sellerEmail(detail) || `Issued ${formatDocumentDate(issuedDate(detail))}`}</p></div><div className="finance-total"><span>Total amount</span><strong>{formatMoney(detail.amount, detail.currency)}</strong><small>{displayValue(detail.currency)}</small></div></div><div className="finance-detail-grid">{[["Document Number", detail.documentNumber || detail.document_number], ["Seller ID", detail.sellerId ?? detail.seller_id], ["Order Number", orderNumber(detail)], ["Order Status", normalizeLabel(detail.orderStatus || detail.order_status)], ["Payout Batch", payoutBatch(detail)], ["Status", normalizeLabel(detail.status || detail.paymentStatus)], ["Issued At", formatDocumentDate(detail.issuedAt || detail.issued_at)], ["Last Updated", formatDocumentDate(detail.updatedAt || detail.updated_at)]].map(([label, value]) => <div key={label}><small>{label}</small><strong>{displayValue(value)}</strong></div>)}</div>{(String(detail.status).toLowerCase() === "issued" || detail.issued === true) && <div className="finance-lock-notice"><LockKeyhole size={18} /><div><strong>Issued and locked</strong><span>This financial document is immutable and available in read-only mode.</span></div></div>}</>}</Modal.Body><Modal.Footer><button className="finance-btn ghost" onClick={() => setDetail(null)}>Close</button>{detail && <button className="finance-btn primary" disabled={!!fileAction} onClick={() => downloadPdf(detail)}>{fileAction === `pdf-${documentId(detail)}` ? <Spinner size="sm" /> : <><Download size={16} /> Download PDF</>}</button>}</Modal.Footer></Modal>

    <Modal show={showEmail} onHide={() => !emailLoading && setShowEmail(false)} centered><form onSubmit={submitEmail}><Modal.Header closeButton><Modal.Title>Email Export</Modal.Title></Modal.Header><Modal.Body className="finance-email-form"><label>Export scope<select value={emailForm.scope} onChange={(e) => setEmailForm({ ...emailForm, scope: e.target.value })}><option value="documents">Selected documents ({selected.length})</option><option value="filters">Current filters</option></select></label><label>Format<select value={emailForm.format} onChange={(e) => setEmailForm({ ...emailForm, format: e.target.value })}><option value="csv">CSV</option><option value="xlsx">XLSX</option></select></label><label>Accounting email address<input type="email" required value={emailForm.email} onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })} placeholder="accounts@example.com" /><small>The server will validate this address.</small></label></Modal.Body><Modal.Footer><button type="button" className="finance-btn secondary" disabled={emailLoading} onClick={() => setShowEmail(false)}>Cancel</button><button className="finance-btn primary" disabled={emailLoading}>{emailLoading ? <><Spinner size="sm" /> Requesting…</> : "Email Export"}</button></Modal.Footer></form></Modal>
  </div>;
};

export default FinanceDocumentCentre;
